// TeamManager.js — AVS Cloud Team-Speicher Verwaltung v2
import React, { useState, useEffect, useCallback } from 'react';

const PERM_LABELS = {
  read:       'Lesen & Download',
  write:      'Nur Upload',
  read_write: 'Voll-Zugriff',
};
const PERM_COLORS = {
  read:       '#60a5fa',
  write:      '#fbbf24',
  read_write: '#4ade80',
};

const V = '#a855f7';
const VL = 'rgba(168,85,247,0.15)';
const VB = 'rgba(168,85,247,0.25)';

function TeamManager({ token, onClose, onSwitchStorage }) {
  const [tab, setTab] = useState('owned');
  const [owned, setOwned]     = useState([]);
  const [memberOf, setMemberOf] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [toast, setToast]       = useState(null);

  // Create space modal
  const [showCreate, setShowCreate] = useState(false);
  const [createName, setCreateName]   = useState('');
  const [createSep, setCreateSep]     = useState(false);
  const [createQuota, setCreateQuota] = useState(10); // GB
  const [creating, setCreating]       = useState(false);
  const [isPaid, setIsPaid]           = useState(false);

  // Members management sub-view
  const [manageSpace, setManageSpace] = useState(null); // space object
  const [members, setMembers]         = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [invSearch, setInvSearch]     = useState('');
  const [invPerms, setInvPerms]       = useState('read');
  const [inviting, setInviting]       = useState(false);

  // Rename space
  const [renameSpace, setRenameSpace] = useState(null);
  const [renameVal, setRenameVal]     = useState('');

  // Delete confirm
  const [deleteSpace, setDeleteSpace] = useState(null);
  const [deleting, setDeleting]       = useState(false);

  // Quota resize
  const [quotaEditGb, setQuotaEditGb]   = useState('');
  const [savingQuota, setSavingQuota]   = useState(false);

  // Accept disclaimer
  const [acceptInv, setAcceptInv]           = useState(null);
  const [disclaimerChecked, setDisclaimer]  = useState(false);
  const [accepting, setAccepting]           = useState(false);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  // Body scroll lock
  useEffect(() => {
    const y = window.scrollY;
    document.body.style.cssText = `position:fixed;top:-${y}px;width:100%`;
    return () => {
      document.body.style.cssText = '';
      window.scrollTo(0, y);
    };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sRes, iRes] = await Promise.all([
        fetch('/api/team/spaces',      { headers: { Authorization: `Bearer ${token}` } }),
        fetch('/api/team/invitations', { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      const [sData, iData] = await Promise.all([sRes.json(), iRes.json()]);
      setOwned(sData.owned   || []);
      setMemberOf(sData.memberOf || []);
      setInvitations(iData.invitations || []);

      // Check paid plan by attempting to create with is_separate=true and seeing if server says paid
      // Actually derive from owned spaces having is_separate ones, or store in state from API
      // Simpler: check if user has any active subscription via /api/users/plan or billing endpoint
      // For now, detect from owned spaces
      setIsPaid(false); // will be set by create attempt if needed
      // Check billing status
      try {
        const bRes = await fetch('/api/billing/subscription', { headers: { Authorization: `Bearer ${token}` } });
        if (bRes.ok) {
          const bData = await bRes.json();
          const sub = bData.subscription;
          setIsPaid(!!(sub && ['active', 'trialing'].includes(sub.status)));
        }
      } catch (_) {}
    } catch (_) {
      showToast('Fehler beim Laden', 'error');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const loadMembers = useCallback(async (spaceId) => {
    setLoadingMembers(true);
    try {
      const res = await fetch(`/api/team/spaces/${spaceId}/members`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setMembers(data.members || []);
      setPendingInvites(data.invitations || []);
    } catch (_) {
      showToast('Fehler beim Laden der Mitglieder', 'error');
    } finally {
      setLoadingMembers(false);
    }
  }, [token]);

  const handleCreateSpace = async () => {
    if (!createName.trim()) return;
    setCreating(true);
    try {
      const res = await fetch('/api/team/spaces', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: createName.trim(),
          is_separate: createSep,
          quota_bytes: createSep ? createQuota * 1024 * 1024 * 1024 : undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) return showToast(data.error || 'Fehler', 'error');
      showToast('Team-Speicher erstellt!');
      setShowCreate(false);
      setCreateName('');
      setCreateSep(false);
      setCreateQuota(10);
      await load();
    } catch (_) {
      showToast('Fehler beim Erstellen', 'error');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteSpace = async () => {
    if (!deleteSpace) return;
    setDeleting(true);
    try {
      const res = await fetch(`/api/team/spaces/${deleteSpace.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) return showToast(data.error || 'Fehler', 'error');
      showToast('Team-Speicher gelöscht');
      setDeleteSpace(null);
      await load();
    } catch (_) {
      showToast('Fehler beim Löschen', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const handleRename = async () => {
    if (!renameSpace || !renameVal.trim()) return;
    try {
      const res = await fetch(`/api/team/spaces/${renameSpace.id}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: renameVal.trim() }),
      });
      const data = await res.json();
      if (!res.ok) return showToast(data.error || 'Fehler', 'error');
      showToast('Umbenannt!');
      setRenameSpace(null);
      await load();
      if (manageSpace && manageSpace.id === renameSpace.id) {
        setManageSpace(prev => ({ ...prev, name: renameVal.trim() }));
      }
    } catch (_) {
      showToast('Fehler beim Umbenennen', 'error');
    }
  };

  const handleInvite = async () => {
    if (!invSearch.trim() || !manageSpace) return;
    setInviting(true);
    try {
      const res = await fetch(`/api/team/spaces/${manageSpace.id}/invite`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ search: invSearch.trim(), permissions: invPerms }),
      });
      const data = await res.json();
      if (!res.ok) return showToast(data.error || 'Fehler', 'error');
      showToast(data.message);
      setInvSearch('');
      await loadMembers(manageSpace.id);
    } catch (_) {
      showToast('Fehler beim Einladen', 'error');
    } finally {
      setInviting(false);
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!manageSpace) return;
    try {
      const res = await fetch(`/api/team/spaces/${manageSpace.id}/members/${memberId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return showToast('Fehler beim Entfernen', 'error');
      showToast('Mitglied entfernt');
      await loadMembers(manageSpace.id);
    } catch (_) {
      showToast('Fehler', 'error');
    }
  };

  const handleChangePermissions = async (memberId, permissions) => {
    if (!manageSpace) return;
    try {
      const res = await fetch(`/api/team/spaces/${manageSpace.id}/members/${memberId}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ permissions }),
      });
      if (!res.ok) return showToast('Fehler beim Ändern', 'error');
      showToast('Berechtigung geändert');
      await loadMembers(manageSpace.id);
    } catch (_) {
      showToast('Fehler', 'error');
    }
  };

  const handleDecline = async (invId) => {
    try {
      await fetch(`/api/team/invitations/${invId}/decline`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      showToast('Einladung abgelehnt');
      await load();
    } catch (_) { showToast('Fehler', 'error'); }
  };

  const handleAccept = async () => {
    if (!disclaimerChecked || !acceptInv) return;
    setAccepting(true);
    try {
      const res = await fetch(`/api/team/invitations/${acceptInv.id}/accept`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ disclaimerAccepted: true }),
      });
      const data = await res.json();
      if (!res.ok) return showToast(data.error || 'Fehler', 'error');
      showToast(`Einladung in "${acceptInv.space_name}" angenommen!`);
      setAcceptInv(null);
      setDisclaimer(false);
      await load();
    } catch (_) {
      showToast('Fehler', 'error');
    } finally {
      setAccepting(false);
    }
  };

  const openManage = (space) => {
    setManageSpace(space);
    setQuotaEditGb(space.quota_bytes ? String(Math.round(space.quota_bytes / (1024 * 1024 * 1024))) : '');
    loadMembers(space.id);
  };

  const handleSaveQuota = async () => {
    if (!manageSpace || !quotaEditGb) return;
    const gb = Number(quotaEditGb);
    if (!gb || gb < 1) return showToast('Mindestgröße: 1 GB', 'error');
    setSavingQuota(true);
    try {
      const res = await fetch(`/api/team/spaces/${manageSpace.id}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ quota_bytes: gb * 1024 * 1024 * 1024 }),
      });
      const data = await res.json();
      if (!res.ok) return showToast(data.error || 'Fehler', 'error');
      setManageSpace(prev => ({ ...prev, quota_bytes: gb * 1024 * 1024 * 1024 }));
      // Update owned list so SpaceCard reflects change immediately
      setOwned(prev => prev.map(s => s.id === manageSpace.id ? { ...s, quota_bytes: gb * 1024 * 1024 * 1024 } : s));
      showToast(`Quota auf ${gb} GB gesetzt`);
    } catch (_) {
      showToast('Netzwerkfehler', 'error');
    } finally {
      setSavingQuota(false);
    }
  };

  const tabs = [
    { id: 'owned',       label: 'Meine Teams',   count: owned.length },
    { id: 'memberOf',    label: 'Mitglied bei',  count: memberOf.length },
    { id: 'invitations', label: 'Einladungen',   count: invitations.length, badge: invitations.length > 0 },
  ];

  const formatGB = (bytes) => bytes ? `${(bytes / 1024 / 1024 / 1024).toFixed(1)} GB` : null;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)', backdropFilter: 'blur(12px)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
      {/* Toast */}
      {toast && (
        <div onClick={() => setToast(null)} style={{
          position: 'fixed', top: '1rem', left: '50%', transform: 'translateX(-50%)',
          zIndex: 9999, maxWidth: '420px', width: '90%', padding: '0.75rem 1.1rem',
          background: toast.type === 'success' ? 'rgba(74,222,128,0.12)' : 'rgba(239,68,68,0.12)',
          border: `1px solid ${toast.type === 'success' ? 'rgba(74,222,128,0.3)' : 'rgba(239,68,68,0.3)'}`,
          borderRadius: '10px', color: toast.type === 'success' ? '#4ade80' : '#f87171',
          fontSize: '0.82rem', cursor: 'pointer', textAlign: 'center', fontWeight: 600,
        }}>{toast.msg}</div>
      )}

      {/* ── Main Panel ── */}
      <div style={{ width: '100%', maxWidth: '660px', maxHeight: 'calc(100vh - 2rem)', display: 'flex', flexDirection: 'column', background: '#0a1628', border: `1px solid ${VB}`, borderRadius: '18px', overflow: 'hidden' }}>

        {/* Header */}
        <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'linear-gradient(135deg, rgba(168,85,247,0.12), rgba(18,112,196,0.08))' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            {manageSpace ? (
              <button onClick={() => setManageSpace(null)} style={{ background: 'none', border: 'none', color: V, cursor: 'pointer', fontSize: '1rem', padding: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                ← <span style={{ fontSize: '0.82rem', fontWeight: 700 }}>Zurück</span>
              </button>
            ) : (
              <>
                <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: VL, border: `1px solid ${VB}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem' }}>👥</div>
                <div>
                  <div style={{ fontFamily: 'var(--font-head)', fontWeight: 800, fontSize: '1rem', color: '#dceeff' }}>Team-Speicher</div>
                  <div style={{ fontSize: '0.7rem', color: '#8892a4' }}>Teams verwalten &amp; teilen</div>
                </div>
              </>
            )}
          </div>
          {manageSpace ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <div style={{ fontWeight: 700, color: '#dceeff', fontSize: '0.9rem' }}>{manageSpace.name}</div>
              <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#8892a4', cursor: 'pointer', fontSize: '1.1rem', padding: '0.25rem' }}>✕</button>
            </div>
          ) : (
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#8892a4', cursor: 'pointer', fontSize: '1.2rem', padding: '0.25rem' }}>✕</button>
          )}
        </div>

        {manageSpace ? (
          /* ── Members Management Sub-View ── */
          <div style={{ overflowY: 'auto', flex: 1, WebkitOverflowScrolling: 'touch' }}>
            {loadingMembers ? (
              <Spinner />
            ) : (
              <div style={{ padding: '1.25rem' }}>
                {/* Quota Resize — nur für separate Spaces */}
                {manageSpace.is_separate && (
                  <div style={{ background: 'rgba(168,85,247,0.06)', border: `1px solid ${VB}`, borderRadius: '12px', padding: '1rem', marginBottom: '1.25rem' }}>
                    <div style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: V, marginBottom: '0.75rem' }}>
                      💾 Speicher-Quota
                    </div>
                    {/* Usage bar */}
                    {manageSpace.quota_bytes > 0 && (() => {
                      const usedPct = Math.min(100, ((manageSpace.used_bytes || 0) / manageSpace.quota_bytes) * 100);
                      const usedGb  = ((manageSpace.used_bytes || 0) / (1024 * 1024 * 1024)).toFixed(2);
                      const totalGb = (manageSpace.quota_bytes / (1024 * 1024 * 1024)).toFixed(0);
                      const over    = (manageSpace.used_bytes || 0) > manageSpace.quota_bytes;
                      return (
                        <div style={{ marginBottom: '0.75rem' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem' }}>
                            <span style={{ fontSize: '0.75rem', color: over ? '#ef4444' : '#aaa' }}>
                              {usedGb} GB belegt von {totalGb} GB
                            </span>
                            <span style={{ fontSize: '0.75rem', fontWeight: 700, color: over ? '#ef4444' : usedPct > 80 ? '#f59e0b' : V }}>
                              {usedPct.toFixed(1)}%
                            </span>
                          </div>
                          <div style={{ height: '5px', background: 'rgba(168,85,247,0.1)', borderRadius: '3px', overflow: 'hidden' }}>
                            <div style={{
                              height: '100%', width: `${usedPct}%`, borderRadius: '3px', transition: 'width 0.4s',
                              background: over ? 'linear-gradient(90deg,#dc2626,#991b1b)' : usedPct > 80 ? 'linear-gradient(90deg,#f59e0b,#d97706)' : `linear-gradient(90deg,${V},#7c3aed)`,
                            }} />
                          </div>
                        </div>
                      );
                    })()}
                    {/* Quota input */}
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                      <div style={{ position: 'relative', flex: 1 }}>
                        <input
                          type="number" min="1" max="9999" value={quotaEditGb}
                          onChange={e => setQuotaEditGb(e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && handleSaveQuota()}
                          placeholder="GB"
                          style={{ width: '100%', padding: '0.6rem 2.5rem 0.6rem 0.75rem', background: 'rgba(8,17,31,0.8)', border: `1px solid ${VB}`, borderRadius: '8px', color: '#dceeff', fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box' }}
                        />
                        <span style={{ position: 'absolute', right: '0.6rem', top: '50%', transform: 'translateY(-50%)', fontSize: '0.72rem', color: '#8892a4', pointerEvents: 'none' }}>GB</span>
                      </div>
                      <button
                        onClick={handleSaveQuota}
                        disabled={savingQuota || !quotaEditGb || Number(quotaEditGb) < 1}
                        style={{ padding: '0.6rem 1rem', borderRadius: '8px', fontSize: '0.8rem', fontWeight: 700, border: 'none', cursor: savingQuota ? 'wait' : 'pointer', background: quotaEditGb && Number(quotaEditGb) >= 1 ? `linear-gradient(135deg,${V},#7c3aed)` : 'rgba(255,255,255,0.05)', color: quotaEditGb && Number(quotaEditGb) >= 1 ? '#fff' : '#8892a4', whiteSpace: 'nowrap' }}
                      >
                        {savingQuota ? '...' : 'Speichern'}
                      </button>
                    </div>
                    {manageSpace.quota_bytes > 0 && Number(quotaEditGb) > 0 && Number(quotaEditGb) * 1024 * 1024 * 1024 < (manageSpace.used_bytes || 0) && (
                      <p style={{ margin: '0.5rem 0 0', fontSize: '0.72rem', color: '#f87171' }}>
                        ⚠ Neue Quota kleiner als aktuell belegter Speicher ({((manageSpace.used_bytes || 0) / (1024 * 1024 * 1024)).toFixed(2)} GB) — Uploads werden gesperrt.
                      </p>
                    )}
                  </div>
                )}

                {/* Current members */}
                <div style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: V, marginBottom: '0.75rem' }}>
                  Mitglieder ({members.length})
                </div>
                {members.length === 0 ? (
                  <EmptyState icon="👥" text="Noch keine Mitglieder" sub="Lade unten jemanden ein." />
                ) : members.map(m => (
                  <div key={m.id} style={{ background: 'rgba(168,85,247,0.06)', border: `1px solid ${VB}`, borderRadius: '12px', padding: '0.9rem', marginBottom: '0.6rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', minWidth: 0 }}>
                        <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: VL, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.9rem', flexShrink: 0 }}>👤</div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 700, color: '#dceeff', fontSize: '0.88rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.member_username}</div>
                          <div style={{ fontSize: '0.68rem', color: '#8892a4' }}>{m.member_email}</div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center' }}>
                        {/* Permission selector */}
                        <select
                          value={m.permissions}
                          onChange={e => handleChangePermissions(m.id, e.target.value)}
                          style={{ padding: '0.3rem 0.5rem', borderRadius: '7px', fontSize: '0.72rem', fontWeight: 600, background: `${PERM_COLORS[m.permissions]}15`, border: `1px solid ${PERM_COLORS[m.permissions]}60`, color: PERM_COLORS[m.permissions], cursor: 'pointer', outline: 'none' }}
                        >
                          {Object.entries(PERM_LABELS).map(([v, l]) => (
                            <option key={v} value={v} style={{ background: '#0a1628', color: PERM_COLORS[v] }}>{l}</option>
                          ))}
                        </select>
                        <button onClick={() => handleRemoveMember(m.id)} style={{ padding: '0.3rem 0.65rem', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: '7px', color: '#f87171', cursor: 'pointer', fontSize: '0.72rem', fontWeight: 600, whiteSpace: 'nowrap' }}>Entfernen</button>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Pending invitations in this space */}
                {pendingInvites.length > 0 && (
                  <>
                    <div style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#fbbf24', marginBottom: '0.6rem', marginTop: '1.25rem' }}>
                      Ausstehende Einladungen ({pendingInvites.length})
                    </div>
                    {pendingInvites.map(inv => (
                      <div key={inv.id} style={{ background: 'rgba(251,191,36,0.05)', border: '1px solid rgba(251,191,36,0.15)', borderRadius: '10px', padding: '0.75rem', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
                        <div>
                          <div style={{ fontWeight: 600, color: '#dceeff', fontSize: '0.82rem' }}>{inv.invitee_username}</div>
                          <div style={{ fontSize: '0.68rem', color: '#8892a4' }}>Läuft ab: {new Date(inv.expires_at).toLocaleDateString('de-DE')}</div>
                        </div>
                        <span style={{ fontSize: '0.68rem', fontWeight: 700, color: PERM_COLORS[inv.permissions], background: `${PERM_COLORS[inv.permissions]}15`, padding: '2px 8px', borderRadius: '20px' }}>{PERM_LABELS[inv.permissions]}</span>
                      </div>
                    ))}
                  </>
                )}

                {/* Invite form */}
                <div style={{ marginTop: '1.5rem', borderTop: '1px solid rgba(255,255,255,0.07)', paddingTop: '1.25rem' }}>
                  <div style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: V, marginBottom: '0.75rem' }}>Mitglied einladen</div>
                  <input
                    value={invSearch} onChange={e => setInvSearch(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleInvite()}
                    placeholder="E-Mail, Username oder Discord-ID"
                    style={{ width: '100%', padding: '0.7rem 1rem', background: 'rgba(8,17,31,0.8)', border: `1px solid ${VB}`, borderRadius: '10px', color: '#dceeff', fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box', marginBottom: '0.75rem', fontFamily: 'var(--font-head)' }}
                  />
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.4rem', marginBottom: '0.75rem' }}>
                    {Object.entries(PERM_LABELS).map(([v, l]) => (
                      <button key={v} onClick={() => setInvPerms(v)} style={{
                        padding: '0.5rem', borderRadius: '8px', fontSize: '0.72rem', fontWeight: 600,
                        border: `1px solid ${invPerms === v ? PERM_COLORS[v] : 'rgba(255,255,255,0.08)'}`,
                        background: invPerms === v ? `${PERM_COLORS[v]}18` : 'rgba(255,255,255,0.03)',
                        color: invPerms === v ? PERM_COLORS[v] : '#8892a4', cursor: 'pointer',
                      }}>{l}</button>
                    ))}
                  </div>
                  <button onClick={handleInvite} disabled={inviting || !invSearch.trim()} style={{
                    width: '100%', padding: '0.75rem', borderRadius: '10px', fontSize: '0.875rem', fontWeight: 700,
                    background: invSearch.trim() ? 'linear-gradient(135deg,#7c3aed,#a855f7)' : 'rgba(255,255,255,0.05)',
                    color: invSearch.trim() ? '#fff' : '#8892a4', border: 'none',
                    cursor: inviting || !invSearch.trim() ? 'not-allowed' : 'pointer',
                  }}>
                    {inviting ? 'Sende...' : 'Einladung senden'}
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <>
            {/* ── Tabs ── */}
            <div style={{ display: 'flex', borderBottom: '1px solid rgba(255,255,255,0.07)', overflowX: 'auto', WebkitOverflowScrolling: 'touch', flexShrink: 0 }}>
              {tabs.map(t => (
                <button key={t.id} onClick={() => setTab(t.id)} style={{
                  padding: '0.75rem 1rem', whiteSpace: 'nowrap', fontSize: '0.8rem', fontWeight: 600,
                  fontFamily: 'var(--font-head)', border: 'none', cursor: 'pointer',
                  background: tab === t.id ? VL : 'none',
                  color: tab === t.id ? V : '#8892a4',
                  borderBottom: tab === t.id ? `2px solid ${V}` : '2px solid transparent',
                  transition: 'all 0.2s',
                }}>
                  {t.label}
                  {t.count > 0 && (
                    <span style={{ marginLeft: '5px', background: t.badge ? '#ef4444' : VL, color: t.badge ? '#fff' : V, borderRadius: '20px', padding: '1px 6px', fontSize: '0.65rem', fontWeight: 700 }}>{t.count}</span>
                  )}
                </button>
              ))}
            </div>

            {/* ── Content ── */}
            <div style={{ overflowY: 'auto', flex: 1, WebkitOverflowScrolling: 'touch' }}>
              {loading ? <Spinner /> : (
                <>
                  {/* TAB: Meine Teams */}
                  {tab === 'owned' && (
                    <div style={{ padding: '1.25rem' }}>
                      {/* Create button */}
                      <button onClick={() => setShowCreate(true)} style={{
                        width: '100%', padding: '0.75rem', borderRadius: '12px', marginBottom: '1rem',
                        border: `1px dashed ${VB}`, background: VL, color: V,
                        fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font-head)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
                      }}>
                        + Neuen Team-Speicher erstellen
                      </button>

                      {owned.length === 0 ? (
                        <EmptyState icon="📦" text="Noch keine Team-Speicher" sub='Klicke auf "Neuen Team-Speicher erstellen" um loszulegen.' />
                      ) : owned.map(space => (
                        <SpaceCard
                          key={space.id}
                          space={space}
                          isOwner
                          onOpen={() => {
                            window.location.href = `/team/${space.id}`;
                          }}
                          onManage={() => openManage(space)}
                          onRename={() => { setRenameSpace(space); setRenameVal(space.name); }}
                          onDelete={() => setDeleteSpace(space)}
                          formatGB={formatGB}
                        />
                      ))}
                    </div>
                  )}

                  {/* TAB: Mitglied bei */}
                  {tab === 'memberOf' && (
                    <div style={{ padding: '1.25rem' }}>
                      {memberOf.length === 0 ? (
                        <EmptyState icon="🔒" text="Du bist noch in keinem Team" sub="Warte auf eine Einladung eines anderen Nutzers." />
                      ) : memberOf.map(space => (
                        <SpaceCard
                          key={space.id}
                          space={space}
                          isOwner={false}
                          myPermissions={space.my_permissions}
                          onOpen={() => {
                            window.location.href = `/team/${space.id}`;
                          }}
                          formatGB={formatGB}
                        />
                      ))}
                    </div>
                  )}

                  {/* TAB: Einladungen */}
                  {tab === 'invitations' && (
                    <div style={{ padding: '1.25rem' }}>
                      {invitations.length === 0 ? (
                        <EmptyState icon="📭" text="Keine offenen Einladungen" sub="Du hast keine ausstehenden Team-Einladungen." />
                      ) : invitations.map(inv => (
                        <div key={inv.id} style={{ background: 'rgba(251,191,36,0.06)', border: '1px solid rgba(251,191,36,0.2)', borderRadius: '12px', padding: '1rem', marginBottom: '0.75rem' }}>
                          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem' }}>
                            <div>
                              <div style={{ fontWeight: 700, color: '#dceeff', fontSize: '0.9rem', marginBottom: '0.25rem' }}>
                                {inv.inviter_username} lädt dich ein
                              </div>
                              <div style={{ fontSize: '0.78rem', color: V, fontWeight: 600, marginBottom: '0.25rem' }}>📦 {inv.space_name}</div>
                              <div style={{ fontSize: '0.72rem', color: '#8892a4', marginBottom: '0.35rem' }}>
                                Berechtigung: <span style={{ color: PERM_COLORS[inv.permissions], fontWeight: 600 }}>{PERM_LABELS[inv.permissions]}</span>
                              </div>
                              <div style={{ fontSize: '0.68rem', color: '#8892a4' }}>Läuft ab: {new Date(inv.expires_at).toLocaleDateString('de-DE')}</div>
                            </div>
                            <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                              <button onClick={() => { setAcceptInv(inv); setDisclaimer(false); }} style={{ padding: '0.45rem 0.9rem', background: 'rgba(74,222,128,0.1)', border: '1px solid rgba(74,222,128,0.3)', borderRadius: '8px', color: '#4ade80', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600 }}>Annehmen</button>
                              <button onClick={() => handleDecline(inv.id)} style={{ padding: '0.45rem 0.9rem', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '8px', color: '#f87171', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600 }}>Ablehnen</button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </div>

      {/* ── Create Space Modal ── */}
      {showCreate && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div style={{ width: '100%', maxWidth: '480px', background: '#0a1628', border: `1px solid ${VB}`, borderRadius: '16px', padding: '1.75rem' }}>
            <h3 style={{ fontFamily: 'var(--font-head)', fontWeight: 800, fontSize: '1.1rem', color: '#dceeff', marginBottom: '1.25rem' }}>Neuen Team-Speicher erstellen</h3>

            <label style={labelStyle}>Name</label>
            <input
              value={createName} onChange={e => setCreateName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !createSep && handleCreateSpace()}
              maxLength={80} placeholder="z.B. Projektteam Alpha"
              style={inputStyle}
              autoFocus
            />

            {isPaid ? (
              <>
                <button onClick={() => setCreateSep(!createSep)} style={{
                  width: '100%', padding: '0.75rem 1rem', borderRadius: '10px', marginBottom: '0.75rem',
                  fontSize: '0.85rem', fontWeight: 600,
                  border: `1px solid ${createSep ? VB : 'rgba(255,255,255,0.08)'}`,
                  background: createSep ? VL : 'rgba(255,255,255,0.03)',
                  color: createSep ? V : '#8892a4', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}>
                  <span>Separater Team-Speicher (eigener Ordner)</span>
                  <span style={{ fontSize: '0.7rem' }}>{createSep ? '✓ Aktiv' : 'Optional'}</span>
                </button>
                {createSep && (
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={labelStyle}>Quota</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <input
                        type="number" min="1" max="1000"
                        value={createQuota} onChange={e => setCreateQuota(Number(e.target.value))}
                        style={{ ...inputStyle, width: '80px', marginBottom: 0 }}
                      />
                      <span style={{ color: '#8892a4', fontSize: '0.85rem' }}>GB</span>
                    </div>
                  </div>
                )}
                {!createSep && (
                  <p style={{ fontSize: '0.72rem', color: '#8892a4', lineHeight: 1.6, marginBottom: '1rem' }}>
                    Ohne separaten Ordner sehen Mitglieder deine privaten Dateien.
                  </p>
                )}
              </>
            ) : (
              <div style={{ background: 'rgba(168,85,247,0.06)', border: `1px solid ${VB}`, borderRadius: '10px', padding: '0.85rem', marginBottom: '1rem' }}>
                <p style={{ fontSize: '0.78rem', color: '#8892a4', lineHeight: 1.6, margin: 0 }}>
                  ℹ️ <strong style={{ color: V }}>Free Plan:</strong> Mitglieder sehen deine privaten Dateien. Upgrade auf einen Bezahl-Plan für einen separaten Team-Ordner.
                </p>
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button onClick={handleCreateSpace} disabled={creating || !createName.trim()} style={{
                flex: 1, padding: '0.75rem', borderRadius: '10px', fontWeight: 700, fontSize: '0.875rem',
                background: createName.trim() ? 'linear-gradient(135deg,#7c3aed,#a855f7)' : 'rgba(255,255,255,0.05)',
                color: createName.trim() ? '#fff' : '#8892a4', border: 'none',
                cursor: creating || !createName.trim() ? 'not-allowed' : 'pointer',
              }}>
                {creating ? 'Erstelle...' : 'Erstellen'}
              </button>
              <button onClick={() => setShowCreate(false)} style={{ padding: '0.75rem 1.25rem', borderRadius: '10px', background: 'none', border: '1px solid rgba(255,255,255,0.1)', color: '#8892a4', cursor: 'pointer', fontSize: '0.875rem' }}>Abbrechen</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Rename Modal ── */}
      {renameSpace && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div style={{ width: '100%', maxWidth: '420px', background: '#0a1628', border: `1px solid ${VB}`, borderRadius: '16px', padding: '1.5rem' }}>
            <h3 style={{ fontFamily: 'var(--font-head)', fontWeight: 700, fontSize: '1rem', color: '#dceeff', marginBottom: '1rem' }}>Team-Speicher umbenennen</h3>
            <input
              value={renameVal} onChange={e => setRenameVal(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleRename()}
              maxLength={80} autoFocus
              style={inputStyle}
            />
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button onClick={handleRename} style={{ flex: 1, padding: '0.7rem', borderRadius: '10px', fontWeight: 700, fontSize: '0.875rem', background: 'linear-gradient(135deg,#7c3aed,#a855f7)', color: '#fff', border: 'none', cursor: 'pointer' }}>Speichern</button>
              <button onClick={() => setRenameSpace(null)} style={{ padding: '0.7rem 1.25rem', borderRadius: '10px', background: 'none', border: '1px solid rgba(255,255,255,0.1)', color: '#8892a4', cursor: 'pointer', fontSize: '0.875rem' }}>Abbrechen</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Confirm Modal ── */}
      {deleteSpace && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div style={{ width: '100%', maxWidth: '420px', background: '#0a1628', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '16px', padding: '1.75rem' }}>
            <h3 style={{ fontFamily: 'var(--font-head)', fontWeight: 800, fontSize: '1rem', color: '#f87171', marginBottom: '0.75rem' }}>Team-Speicher löschen?</h3>
            <p style={{ color: '#8892a4', fontSize: '0.83rem', lineHeight: 1.6, marginBottom: '1.25rem' }}>
              "<strong style={{ color: '#dceeff' }}>{deleteSpace.name}</strong>" wird permanent gelöscht.
              {deleteSpace.is_separate && <span style={{ color: '#f87171' }}> Alle darin enthaltenen Dateien werden ebenfalls gelöscht!</span>}
            </p>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button onClick={handleDeleteSpace} disabled={deleting} style={{ flex: 1, padding: '0.75rem', borderRadius: '10px', fontWeight: 700, fontSize: '0.875rem', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', color: '#f87171', cursor: deleting ? 'not-allowed' : 'pointer' }}>
                {deleting ? 'Löschen...' : 'Löschen'}
              </button>
              <button onClick={() => setDeleteSpace(null)} style={{ padding: '0.75rem 1.25rem', borderRadius: '10px', background: 'none', border: '1px solid rgba(255,255,255,0.1)', color: '#8892a4', cursor: 'pointer', fontSize: '0.875rem' }}>Abbrechen</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Accept Invitation Disclaimer Modal ── */}
      {acceptInv && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div style={{ width: '100%', maxWidth: '460px', background: '#0a1628', border: '1px solid rgba(74,222,128,0.25)', borderRadius: '16px', padding: '1.75rem' }}>
            <h3 style={{ fontFamily: 'var(--font-head)', fontWeight: 800, fontSize: '1.1rem', color: '#dceeff', marginBottom: '0.5rem' }}>Einladung annehmen</h3>
            <p style={{ color: '#8892a4', fontSize: '0.83rem', lineHeight: 1.6, marginBottom: '1.25rem' }}>
              <strong style={{ color: '#1ab4f0' }}>{acceptInv.inviter_username}</strong> gibt dir <strong style={{ color: PERM_COLORS[acceptInv.permissions] }}>{PERM_LABELS[acceptInv.permissions]}</strong>-Zugriff auf den Team-Speicher <strong style={{ color: V }}>"{acceptInv.space_name}"</strong>.
            </p>
            <div style={{ background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '10px', padding: '1rem', marginBottom: '1.25rem' }}>
              <p style={{ color: '#fca5a5', fontSize: '0.78rem', lineHeight: 1.7, margin: 0 }}>
                ⚠️ <strong>Wichtiger Hinweis:</strong> {acceptInv.inviter_username} kann deinen Zugriff <strong>jederzeit widerrufen</strong>. AquaV Services haftet nicht für Datenverluste.
              </p>
            </div>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer', marginBottom: '1.25rem' }}>
              <input type="checkbox" checked={disclaimerChecked} onChange={e => setDisclaimer(e.target.checked)} style={{ marginTop: '2px', accentColor: '#4ade80', width: '16px', height: '16px', flexShrink: 0 }} />
              <span style={{ fontSize: '0.8rem', color: '#8892a4', lineHeight: 1.6 }}>Ich habe den Hinweis gelesen und akzeptiere, dass mein Zugriff jederzeit entzogen werden kann.</span>
            </label>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button onClick={handleAccept} disabled={!disclaimerChecked || accepting} style={{ flex: 1, padding: '0.75rem', borderRadius: '10px', fontWeight: 700, fontSize: '0.875rem', background: disclaimerChecked ? 'linear-gradient(135deg,#16a34a,#15803d)' : 'rgba(255,255,255,0.05)', color: disclaimerChecked ? '#fff' : '#8892a4', border: 'none', cursor: disclaimerChecked ? 'pointer' : 'not-allowed' }}>
                {accepting ? 'Annehmen...' : 'Annehmen'}
              </button>
              <button onClick={() => setAcceptInv(null)} style={{ padding: '0.75rem 1.25rem', borderRadius: '10px', background: 'none', border: '1px solid rgba(255,255,255,0.1)', color: '#8892a4', cursor: 'pointer', fontSize: '0.875rem' }}>Abbrechen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Space Card ──
function SpaceCard({ space, isOwner, myPermissions, onManage, onRename, onDelete, onOpen, formatGB }) {
  const usedPct = space.is_separate && space.quota_bytes
    ? Math.min(100, ((space.used_bytes || 0) / space.quota_bytes) * 100)
    : null;

  return (
    <div style={{ background: 'rgba(168,85,247,0.06)', border: '1px solid rgba(168,85,247,0.18)', borderRadius: '14px', padding: '1rem', marginBottom: '0.75rem' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.35rem' }}>
            <span style={{ fontWeight: 700, color: '#dceeff', fontSize: '0.92rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{space.name}</span>
            {space.is_separate ? (
              <span style={{ fontSize: '0.62rem', fontWeight: 700, background: 'rgba(168,85,247,0.15)', border: '1px solid rgba(168,85,247,0.3)', color: '#c084fc', padding: '1px 7px', borderRadius: '20px' }}>Separat</span>
            ) : (
              <span style={{ fontSize: '0.62rem', fontWeight: 700, background: 'rgba(96,165,250,0.1)', border: '1px solid rgba(96,165,250,0.2)', color: '#60a5fa', padding: '1px 7px', borderRadius: '20px' }}>Free</span>
            )}
            {space.status === 'locked' && (
              <span style={{ fontSize: '0.62rem', fontWeight: 700, background: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.3)', color: '#fbbf24', padding: '1px 7px', borderRadius: '20px' }}>Gesperrt</span>
            )}
            {space.status === 'pending_delete' && (
              <span style={{ fontSize: '0.62rem', fontWeight: 700, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#f87171', padding: '1px 7px', borderRadius: '20px' }}>Löschung ausstehend</span>
            )}
          </div>
          {!isOwner && (
            <div style={{ fontSize: '0.72rem', color: '#8892a4', marginBottom: '0.3rem' }}>Inhaber: <span style={{ color: '#dceeff' }}>{space.owner_username}</span></div>
          )}
          {isOwner && space.member_count !== undefined && (
            <div style={{ fontSize: '0.72rem', color: '#8892a4', marginBottom: '0.3rem' }}>
              {space.member_count} {space.member_count === 1 ? 'Mitglied' : 'Mitglieder'}
            </div>
          )}
          {myPermissions && (
            <span style={{ fontSize: '0.68rem', fontWeight: 700, color: PERM_COLORS[myPermissions], background: `${PERM_COLORS[myPermissions]}15`, padding: '2px 8px', borderRadius: '20px', display: 'inline-block' }}>{PERM_LABELS[myPermissions]}</span>
          )}
          {/* Quota bar for separate spaces */}
          {space.is_separate && space.quota_bytes && (
            <div style={{ marginTop: '0.5rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.68rem', color: '#8892a4', marginBottom: '0.25rem' }}>
                <span>{formatGB(space.used_bytes || 0)} verwendet</span>
                <span>{formatGB(space.quota_bytes)} gesamt</span>
              </div>
              <div style={{ height: '4px', background: 'rgba(255,255,255,0.06)', borderRadius: '4px' }}>
                <div style={{ height: '100%', width: `${usedPct}%`, background: usedPct > 80 ? '#ef4444' : '#a855f7', borderRadius: '4px', transition: 'width 0.3s' }} />
              </div>
            </div>
          )}
        </div>

        {/* Buttons */}
        <div style={{ display: 'flex', gap: '0.4rem', flexShrink: 0, flexWrap: 'wrap' }}>
          {isOwner ? (
            <>
              {space.status === 'active' && (
                <button onClick={onOpen} style={{ padding: '0.4rem 0.85rem', background: 'rgba(168,85,247,0.15)', border: '1px solid rgba(168,85,247,0.35)', borderRadius: '8px', color: '#a855f7', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 700 }}>Öffnen →</button>
              )}
              <button onClick={onManage} style={actionBtn('#a855f7')} title="Mitglieder">👥</button>
              <button onClick={onRename} style={actionBtn('#60a5fa')} title="Umbenennen">✎</button>
              <button onClick={onDelete} style={actionBtn('#ef4444')} title="Löschen">🗑</button>
            </>
          ) : (
            space.status === 'active' && myPermissions !== 'write' && (
              <button onClick={onOpen} style={{ padding: '0.4rem 0.85rem', background: 'rgba(168,85,247,0.15)', border: '1px solid rgba(168,85,247,0.35)', borderRadius: '8px', color: '#a855f7', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 700 }}>Öffnen →</button>
            )
          )}
        </div>
      </div>
    </div>
  );
}

function Spinner() {
  return (
    <div style={{ textAlign: 'center', padding: '3rem' }}>
      <div style={{ width: '36px', height: '36px', border: '3px solid rgba(168,85,247,0.2)', borderTop: '3px solid #a855f7', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 1rem' }} />
      <p style={{ color: '#8892a4', fontSize: '0.875rem' }}>Lade...</p>
    </div>
  );
}

function EmptyState({ icon, text, sub }) {
  return (
    <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
      <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>{icon}</div>
      <p style={{ color: '#dceeff', fontSize: '0.9rem', fontWeight: 600, marginBottom: '0.4rem' }}>{text}</p>
      <p style={{ color: '#8892a4', fontSize: '0.8rem', lineHeight: 1.6 }}>{sub}</p>
    </div>
  );
}

const labelStyle = {
  display: 'block', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.1em',
  textTransform: 'uppercase', color: '#a855f7', marginBottom: '0.5rem',
};

const inputStyle = {
  width: '100%', padding: '0.75rem 1rem', background: 'rgba(8,17,31,0.8)',
  border: '1px solid rgba(168,85,247,0.2)', borderRadius: '10px', color: '#dceeff',
  fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box', marginBottom: '1rem',
  fontFamily: 'var(--font-head)',
};

function actionBtn(color) {
  return {
    padding: '0.4rem 0.65rem', background: `${color}18`, border: `1px solid ${color}40`,
    borderRadius: '8px', color, cursor: 'pointer', fontSize: '0.85rem',
  };
}

export default TeamManager;
